/** Token'lı fetch — sayfalardaki ham fetch('/api/...') çağrıları için */
export async function authFetch(input: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('auth_token');
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(input, {
    ...options,
    headers,
  });

  if (response.status === 401 && !input.includes('/login')) {
    localStorage.removeItem('auth_token');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  return response;
}

// Facility type definition
export interface Facility {
  id: number;
  name: string;
  description?: string;
  status?: string;
  tvCount?: number;
  control_count?: number;
  totalWorks?: number;
  created_at?: string;
  updated_at?: string;
}

// ControlItem type definition
export interface ControlItem {
  id: string | number;
  displayId?: number;
  recordNo?: number; // Kayıt numarası
  title?: string;
  description?: string;
  period?: string;
  frequency?: string; // Backend'de frequency alanı da kullanılıyor
  date: string;
  facility_id?: number;
  facilityId?: string;
  work_done?: string;
  workDone?: string;
  user?: string;
  user_name?: string; // Backend'den gelen user_name alanı
  item_name?: string; // Backend'den gelen item_name alanı
  status?: string;
  approval_status?: string; // Onay durumu
  completion_date?: string; // Tamamlanma tarihi
  created_at?: string;
  table_name?: string;
  item_type?: string;
}

export interface Message {
  id?: number;
  date: string;
  totalCount: number;
  pulledCount: number;
  returnCount: number;
  description?: string;
  account: string;
  sender: string;
}

class ApiService {
  // Güvenlik seviyesi kontrolü
  private isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('localhost');
  private isProduction = !this.isDevelopment;

  // Güvenli log fonksiyonu
  private log(level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any) {
    // Development'ta tüm logları göster
    if (this.isDevelopment) {
      console[level](`[${level.toUpperCase()}] ${message}`, data || '');
      return;
    }

    // Production'da hassas bilgileri gizle
    if (this.isProduction) {
      // Sadece error ve warn loglarını göster, ama hassas verileri gizle
      if (level === 'error' || level === 'warn') {
        // Hassas alanları temizle
        const sanitizedData = this.sanitizeData(data);
        console[level](`[${level.toUpperCase()}] ${message}`, sanitizedData || '');
      }
      // Debug ve info loglarını tamamen gizle
      return;
    }
  }

  // Hassas verileri temizle
  private sanitizeData(data: any): any {
    if (!data) return data;
    
    // Hassas alanları liste
    const sensitiveFields = ['password', 'token', 'email', 'id', 'permissions', 'role'];
    
    if (typeof data === 'object') {
      const sanitized = { ...data };
      sensitiveFields.forEach(field => {
        if (sanitized[field]) {
          sanitized[field] = '***HIDDEN***';
        }
      });
      return sanitized;
    }
    
    return data;
  }

  // Regular Facility endpoints
  async getFacilities(): Promise<Facility[]> {
    this.log('debug', 'Facilities requested');
    return this.request<Facility[]>('/facilities');
  }

  async getFacilitiesWithControlCounts(): Promise<(Facility & { control_count: number })[]> {
    this.log('debug', 'Facilities with control counts requested');
    return this.request<(Facility & { control_count: number })[]>('/facilities?withControlCounts=true');
  }

  async getFacilitiesWithCompletedWorks(): Promise<(Facility & { completed_works: number })[]> {
    this.log('debug', 'Facilities with completed works requested');
    return this.request<(Facility & { completed_works: number })[]>('/facilities?withCompletedWorks=true');
  }

  async createFacility(facility: { name: string; description?: string; status?: string }): Promise<Facility> {
    this.log('info', 'Creating facility', facility);
    return this.request<Facility>('/facilities', {
      method: 'POST',
      body: JSON.stringify(facility),
    });
  }

  async updateFacility(id: number, facility: { name: string; description?: string; status?: string }): Promise<Facility> {
    this.log('info', `Updating facility ${id}`, facility);
    return this.request<Facility>(`/facilities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(facility),
    });
  }

  async deleteFacility(id: number): Promise<any> {
    this.log('warn', `Deleting facility ${id}`);
    return this.request<any>(`/facilities/${id}`, {
      method: 'DELETE',
    });
  }

  // ControlItem endpoints
  async getControlItems(params?: { period?: string; user?: string }): Promise<ControlItem[]> {
    this.log('debug', 'Control items requested', params);
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.user) queryParams.append('user', params.user);
    
    const queryString = queryParams.toString();
    const url = `/control-items${queryString ? `?${queryString}` : ''}`;
    return this.request<ControlItem[]>(url);
  }

  async createControlItem(item: { title: string; description?: string; period: string; date: string; facilityId?: string; workDone?: string; user?: string; status?: string; completionDate?: string }): Promise<ControlItem> {
    this.log('info', 'Creating control item', item);
    return this.request<ControlItem>('/control-items', {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateControlItem(id: number, item: { title: string; description?: string; period: string; date: string; facilityId?: string; workDone?: string; user?: string; status?: string; completionDate?: string }): Promise<ControlItem> {
    this.log('info', `Updating control item ${id}`, item);
    return this.request<ControlItem>(`/control-items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteControlItem(id: number): Promise<any> {
    this.log('warn', `Deleting control item ${id}`);
    return this.request<any>(`/control-items/${id}`, {
      method: 'DELETE',
    });
  }

  async moveControlItems(params: { 
    sourcePeriod: string; 
    targetPeriod: string; 
    startDate?: string; 
    endDate?: string; 
  }): Promise<{ message: string; movedCount: number }> {
    this.log('info', 'Moving control items', params);
    return this.request<{ message: string; movedCount: number }>('/control-items/move', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async getPendingApprovals(user?: string): Promise<ControlItem[]> {
    this.log('debug', 'Pending approvals requested', { user });
    return this.request<ControlItem[]>(`/control-items/pending-approvals${user ? `?user=${user}` : ''}`);
  }

  async approveControlItem(id: string, approvedBy: string): Promise<any> {
    this.log('info', `Approving control item ${id}`, { approvedBy });
    return this.request<any>(`/control-items/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ approvedBy }),
    });
  }

  async rejectControlItem(id: string, rejectedBy: string, reason: string): Promise<any> {
    this.log('warn', `Rejecting control item ${id}`, { rejectedBy, reason });
    return this.request<any>(`/control-items/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ rejectedBy, reason }),
    });
  }



  // Message endpoints
  async getMessages(): Promise<Message[]> {
    this.log('debug', 'Getting messages');
    const messages = await this.request<any[]>('/messages');
    this.log('debug', 'Messages response:', messages);
    
    // Backend'den gelen snake_case formatını frontend'in camelCase formatına çevir
    return messages.map(message => ({
      id: message.id,
      date: message.date,
      totalCount: message.total_count || 0,
      pulledCount: message.pulled_count || 0,
      returnCount: message.return_count || 0,
      description: message.description,
      account: message.account,
      sender: message.sender
    }));
  }

  async createMessage(message: Omit<Message, 'id'>): Promise<Message> {
    this.log('debug', 'Creating message:', message);
    
    const response = await this.request<any>('/messages', {
      method: 'POST',
      body: JSON.stringify(message),
    });
    this.log('debug', 'Message created:', response);
    
    return response;
  }

  async updateMessage(id: number, message: Omit<Message, 'id'>): Promise<Message> {
    this.log('debug', `Updating message ${id}:`, message);
    
    const response = await this.request<any>(`/messages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(message),
    });
    this.log('debug', 'Message updated:', response);
    
    return response;
  }

  async deleteMessage(id: number): Promise<any> {
    this.log('warn', `Deleting message ${id}`);
    return this.request<any>(`/messages/${id}`, {
      method: 'DELETE',
    });
  }

  // Users endpoints
  async getUsers(): Promise<any[]> {
    this.log('debug', 'Users requested');
    return this.request<any[]>('/users');
  }

  /** Dropdown için güvenli liste (admin şart değil) */
  async getUserOptions(): Promise<Array<{ id: number; username: string; role?: string }>> {
    this.log('debug', 'User options requested');
    return this.request<Array<{ id: number; username: string; role?: string }>>('/users/options');
  }

  async createUser(user: { username: string; email: string; password: string; role?: string; permissions?: string[] }): Promise<any> {
    this.log('info', 'Creating user', user);
    return this.request<any>('/users', {
      method: 'POST',
      body: JSON.stringify(user),
    });
  }

  async updateUser(id: number, user: { username: string; email: string; password?: string; role: string; permissions?: string[] }): Promise<any> {
    this.log('info', `Updating user ${id}`, user);
    return this.request<any>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(user),
    });
  }

  async deleteUser(id: number): Promise<any> {
    this.log('warn', `Deleting user ${id}`);
    return this.request<any>(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async getMessageStats(): Promise<{
    totalMessages: number;
    totalCount: number;
    pulledCount: number;
    successRate: number;
    messageLog: number;
  }> {
    this.log('debug', 'Message stats requested');
    return this.request<{
      totalMessages: number;
      totalCount: number;
      pulledCount: number;
      successRate: number;
      messageLog: number;
    }>('/messages/stats');
  }

  // BagTV endpoints
  async getBagTVFacilities(): Promise<any[]> {
    this.log('debug', 'BagTV facilities requested');
    return this.request<any[]>('/bagtv-facilities');
  }

  async getBagTVStats(): Promise<{
    totalFacilities: number;
    totalTV: number;
    activeFacilities: number;
    averageTV: number;
  }> {
    this.log('debug', 'BagTV stats requested');
    return this.request('/bagtv-facilities?action=stats');
  }

  async getBagTVFacilitiesWithControlCounts(): Promise<any[]> {
    this.log('debug', 'BagTV facilities with control counts requested');
    console.log('API Service: Calling /bagtv-facilities?withControlCounts=true');
    const result = await this.request<any[]>('/bagtv-facilities?withControlCounts=true');
    console.log('API Service: Received result:', result);
    return result;
  }

  async createBagTVFacility(facility: { name: string; tvCount: number; description: string; status: string }): Promise<any> {
    this.log('info', 'Creating BagTV facility', facility);
    return this.request<any>('/bagtv-facilities', {
      method: 'POST',
      body: JSON.stringify(facility),
    });
  }

  async updateBagTVFacility(id: string, facility: { name: string; tvCount: number; description: string; status: string }): Promise<any> {
    this.log('info', `Updating BagTV facility ${id}`, facility);
    return this.request<any>(`/bagtv-facilities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(facility),
    });
  }

  async deleteBagTVFacility(id: string): Promise<any> {
    this.log('warn', `Deleting BagTV facility ${id}`);
    return this.request<any>(`/bagtv-facilities/${id}`, {
      method: 'DELETE',
    });
  }

  async getBagTVControls(facilityId?: string): Promise<any[]> {
    this.log('debug', 'BagTV controls requested', { facilityId });
    return this.request<any[]>(`/bagtv-controls-new${facilityId ? `?facilityId=${facilityId}` : ''}`);
  }

  async createBagTVControl(control: { facilityId: string; date: string; action: string; description?: string; checkedBy: string }): Promise<any> {
    this.log('info', 'Creating BagTV control', control);
    return this.request<any>('/bagtv-controls-new', {
      method: 'POST',
      body: JSON.stringify(control),
    });
  }

  async deleteBagTVControl(id: string): Promise<any> {
    this.log('warn', `Deleting BagTV control ${id}`);
    return this.request<any>(`/bagtv-controls-new/${id}`, {
      method: 'DELETE',
    });
  }

  // YBS Work Program endpoints
  async getYBSWorkItems(): Promise<any[]> {
    this.log('debug', 'YBS work items requested');
    return this.request<any[]>('/ybs-work-items');
  }

  async createYBSWorkItem(item: {
    title: string;
    description: string;
    requestDate: string;
    completionDate?: string;
    requestingDepartment: string;
    responsibleUsers: string[];
    jiraNumber?: string;
    status: string;
    approvalStatus: string;
    createdBy: string;
  }): Promise<any> {
    this.log('info', 'Creating YBS work item', item);
    return this.request<any>('/ybs-work-items', {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateYBSWorkItem(id: number, item: {
    title: string;
    description: string;
    requestDate: string;
    completionDate?: string;
    requestingDepartment: string;
    responsibleUsers: string[];
    jiraNumber?: string;
    status: string;
    approvalStatus: string;
  }): Promise<any> {
    this.log('info', `Updating YBS work item ${id}`, item);
    return this.request<any>(`/ybs-work-items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteYBSWorkItem(id: number): Promise<any> {
    this.log('info', `Deleting YBS work item ${id}`);
    return this.request<any>(`/ybs-work-items/${id}`, {
      method: 'DELETE',
    });
  }

  async updateYBSWorkItemApproval(id: number, approval: {
    approvalStatus: string;
    approvedBy: string;
    approvedAt: string;
  }): Promise<any> {
    this.log('info', `Updating YBS work item approval ${id}`, approval);
    return this.request<any>(`/ybs-work-items/${id}/approval`, {
      method: 'PUT',
      body: JSON.stringify(approval),
    });
  }

  // Authentication
  async getCaptcha(): Promise<{ success: boolean; captchaToken: string; image: string }> {
    return this.request('/captcha');
  }

  async login(credentials: {
    username: string;
    password: string;
    captchaToken: string;
    captchaAnswer: string;
  }): Promise<any> {
    this.log('info', 'Login attempt', { username: credentials.username, password: '***' });
    return this.request<any>('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    // Aynı domain'i kullan
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/api${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    
    this.log('debug', `API Request: ${options?.method || 'GET'} ${url}`);

    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string> | undefined),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      ...options,
      headers,
    });
  
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      this.log('error', `API Error: ${response.status}`, error);

      const shouldReLogin =
        !endpoint.includes('/login') &&
        (response.status === 401 || (response.status === 404 && !token && error?.error === 'Not Found'));

      if (shouldReLogin) {
        localStorage.removeItem('auth_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
  
    const data = await response.json();
    this.log('debug', `API Response: ${options?.method || 'GET'} ${url}`, data);
    return data;
  }
 
  // Mock BagTV Controls API
  async mockBagTVControlsAPI(endpoint: string, options?: RequestInit): Promise<any> {
    this.log('debug', `Mock BagTV Controls API: ${endpoint}`, options);
    
    // Simüle edilmiş gecikme
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock data - Global olarak sakla
    if (!(window as any).mockBagTVControls) {
      (window as any).mockBagTVControls = [
        {
          _id: '1',
          facilityId: '1',
          date: '2025-08-05',
          action: 'Sistem kontrolü yapıldı',
          description: 'Tüm TV sistemleri kontrol edildi ve çalışır durumda',
          checkedBy: 'emrah',
          createdAt: new Date()
        },
        {
          _id: '2',
          facilityId: '2',
          date: '2025-08-04',
          action: 'Bakım yapıldı',
          description: 'TV sistemlerinde rutin bakım yapıldı',
          checkedBy: 'admin',
          createdAt: new Date()
        }
      ];
    }
    
    const mockBagTVControls = (window as any).mockBagTVControls;
    
    if (endpoint === '/bagtv-controls' && options?.method === 'GET') {
      // Query parametrelerini parse et
      const urlParams = new URLSearchParams(endpoint.includes('?') ? endpoint.split('?')[1] : '');
      const facilityId = urlParams.get('facilityId');
      
      this.log('debug', `GET request - facilityId: ${facilityId}`);
      
      if (facilityId) {
        // Sadece belirli tesis için kontrolleri filtrele
        const facilityControls = mockBagTVControls.filter((control: any) => {
          this.log('debug', `Comparing control.facilityId (${control.facilityId}) with requested facilityId (${facilityId})`);
          return control.facilityId === facilityId;
        });
        this.log('debug', `Filtered controls for facility ${facilityId}:`, facilityControls);
        return facilityControls;
      } else {
        this.log('debug', 'Returning all controls:', mockBagTVControls);
        return mockBagTVControls;
      }
    }
    
    if (endpoint === '/bagtv-controls' && options?.method === 'POST') {
      const body = JSON.parse(options.body as string);
      this.log('debug', 'POST request body:', body);
      
      const newControl = {
        _id: Date.now().toString(),
        ...body,
        createdAt: new Date()
      };
      
      mockBagTVControls.push(newControl);
      this.log('debug', 'Added new control:', newControl);
      this.log('debug', 'Updated controls list:', mockBagTVControls);
      
      return newControl;
    }
    
    if (endpoint.includes('/bagtv-controls/') && options?.method === 'DELETE') {
      const id = endpoint.split('/').pop();
      const index = mockBagTVControls.findIndex((control: any) => control._id === id);
      
      if (index !== -1) {
        mockBagTVControls.splice(index, 1);
        return { message: 'Control deleted successfully' };
      } else {
        throw new Error('Control not found');
      }
    }
    
    return mockBagTVControls;
  }

  // Gerçek YBS API
  async realYBSAPI(endpoint: string, options?: RequestInit): Promise<any> {
    this.log('debug', `Real YBS API: ${endpoint}`, options);
    
    // Aynı domain'i kullan
    const apiUrl = this.isDevelopment 
      ? `http://localhost:3001/api${endpoint}`
      : `${window.location.origin}/api${endpoint}`;
    
    const response = await fetch(apiUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`YBS API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Mock YBS API (yedek olarak)
  async mockYBSAPI(endpoint: string, options?: RequestInit): Promise<any> {
    this.log('debug', `Mock YBS API: ${endpoint}`, options);
    
    // Simüle edilmiş gecikme
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Local Storage'dan veri yükle veya varsayılan verileri kullan
    const loadYBSData = (): any[] => {
      const stored = localStorage.getItem('ybs_work_items');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (error) {
          console.error('Local storage parse error:', error);
        }
      }
      
      // Varsayılan veriler (Neon'daki gerçek veriler)
      const defaultData = [
        {
          id: 1,
          title: 'tyest',
          description: 'test',
          requestDate: '2025-08-05',
          completionDate: '2025-08-05',
          requestingDepartment: 'bilgi işlem',
          responsibleUsers: ['emrah2', 'Ferhat'],
          jiraNumber: '',
          status: 'pending',
          approvalStatus: 'pending',
          createdBy: 'admin',
          createdAt: '2025-08-05T10:00:00Z',
          updatedAt: '2025-08-05T15:30:00Z'
        },
        {
          id: 2,
          title: '52',
          description: 'aaaaa',
          requestDate: '2025-08-05',
          completionDate: null,
          requestingDepartment: 'özel',
          responsibleUsers: ['emrah2', 'Ferhat'],
          jiraNumber: '',
          status: 'pending',
          approvalStatus: 'pending',
          createdBy: 'admin',
          createdAt: '2025-08-05T09:00:00Z',
          updatedAt: '2025-08-05T14:20:00Z'
        },
        {
          id: 3,
          title: '28',
          description: 'aaaaaaaa',
          requestDate: '2025-08-05',
          completionDate: null,
          requestingDepartment: 'çevre',
          responsibleUsers: ['emrah2', 'emrah1'],
          jiraNumber: '',
          status: 'pending',
          approvalStatus: 'pending',
          createdBy: 'admin',
          createdAt: '2025-08-05T11:30:00Z',
          updatedAt: '2025-08-05T11:30:00Z'
        }
      ];
      
      return defaultData;
    };
    
    // Local Storage'a kaydet
    const saveYBSData = (data: any[]): void => {
      try {
        localStorage.setItem('ybs_work_items', JSON.stringify(data));
      } catch (error) {
        console.error('Local storage save error:', error);
      }
    };
    
    // Veriyi yükle
    let mockData = loadYBSData();
    
    if (endpoint === '/ybs-work-items' && (!options || options.method === 'GET')) {
      return mockData;
    }
    
    if (endpoint === '/ybs-work-items' && options?.method === 'POST') {
      const newItem = JSON.parse(options.body as string);
      const id = Math.max(...mockData.map((item: any) => item.id)) + 1;
      const item = {
        id,
        ...newItem,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      mockData.push(item);
      saveYBSData(mockData); // Local Storage'a kaydet
      return item;
    }
    
    if (endpoint.startsWith('/ybs-work-items/') && options?.method === 'PUT') {
      const id = parseInt(endpoint.split('/').pop()!);
      const updateData = JSON.parse(options.body as string);
      const itemIndex = mockData.findIndex((item: any) => item.id === id);
      
      if (itemIndex === -1) {
        throw new Error('YBS işi bulunamadı');
      }
      
      mockData[itemIndex] = {
        ...mockData[itemIndex],
        ...updateData,
        updatedAt: new Date().toISOString()
      };
      
      saveYBSData(mockData); // Local Storage'a kaydet
      return mockData[itemIndex];
    }
    
    if (endpoint.startsWith('/ybs-work-items/') && options?.method === 'DELETE') {
      const id = parseInt(endpoint.split('/').pop()!);
      const itemIndex = mockData.findIndex((item: any) => item.id === id);
      
      if (itemIndex === -1) {
        throw new Error('YBS işi bulunamadı');
      }
      
      mockData.splice(itemIndex, 1);
      saveYBSData(mockData); // Local Storage'a kaydet
      return { message: 'YBS işi silindi' };
    }
    
    throw new Error(`Unknown YBS endpoint: ${endpoint}`);
  }

  // BağKart Business Info endpoints
  async getBagKartBusinessInfo(): Promise<any> {
    this.log('debug', 'BağKart Business Info requested');
    return this.request<any>('/bagkart-business-info');
  }

  async getBagKartContractReports(): Promise<any[]> {
    this.log('debug', 'BağKart contract reports requested');
    // Limit'i kaldırdık - tüm kayıtları getirmek için çok büyük bir değer kullanıyoruz
    return this.request<any[]>('/bagkart-business-info/contract-reports?limit=10000');
  }

  async createBagKartContractReport(report: {
    date: string;
    appointmentBusinesses: number;
    dailyProtocolBusinesses: number;
    signedContracts: number;
    awaitingResponse: number;
    negativeResponse: number;
    protocolMeetings: number;
    dailyNewCardholders: number;
    dailySpending: number;
    dailyDiscount: number;
  }): Promise<any> {
    this.log('info', 'Creating BağKart contract report', report);
    return this.request<any>('/bagkart-business-info/contract-report', {
      method: 'POST',
      body: JSON.stringify(report),
    });
  }

  async updateBagKartContractReport(id: number, report: {
    date: string;
    appointmentBusinesses: number;
    dailyProtocolBusinesses: number;
    signedContracts: number;
    awaitingResponse: number;
    negativeResponse: number;
    protocolMeetings: number;
    dailyNewCardholders: number;
    dailySpending: number;
    dailyDiscount: number;
  }): Promise<any> {
    this.log('info', `Updating BağKart contract report ${id}`, report);
    return this.request<any>(`/bagkart-business-info/contract-report/${id}`, {
      method: 'PUT',
      body: JSON.stringify(report),
    });
  }

  async deleteBagKartContractReport(id: number): Promise<any> {
    this.log('warn', `Deleting BağKart contract report ${id}`);
    return this.request<any>(`/bagkart-business-info/contract-report/${id}`, {
      method: 'DELETE',
    });
  }

  async createBagKartTable(): Promise<any> {
    this.log('info', 'Creating BağKart contract reports table');
    return this.request<any>('/bagkart-business-info/create-table', {
      method: 'POST',
    });
  }

  async updateBagKartStats(stats: {
    totalCardholders: number;
    totalBusinesses: number;
    totalSpending: number;
    totalDiscount: number;
  }): Promise<any> {
    this.log('info', 'Updating BağKart stats', stats);
    return this.request<any>('/bagkart-business-info/stats', {
      method: 'PUT',
      body: JSON.stringify(stats),
    });
  }
}

const apiService = new ApiService();
export { apiService }; 